'use client';

import classNames from 'classnames';
import type { MouseEvent, ReactNode } from 'react';

import { HEADER_HEIGHT } from '@/components/Header/constants';
import { AUTHORS_CARDS_ID } from '@/src/components/Article/components/AuthorsList/AuthorsList';
import type { Story } from '@/src/types';
import { Avatar } from '@/src/ui/Avatar/Avatar';
import { AvatarGroup } from '@/src/ui/AvatarGroup/AvatarGroup';
import { slugify } from '@/util/strings/slugify';

import styles from './Authors.module.scss';

interface Props {
    className?: string;
    authors: NonNullable<Story['authors']>;
}

export function Authors({ className, authors }: Props) {
    if (authors.length === 0) {
        return null;
    }

    if (authors.length === 1) {
        const [author] = authors;

        return (
            <Link data-article-author={slugify(author.name)}>
                <Avatar
                    name={author.name}
                    image={author.photos?.[0]?.photo}
                    className={styles.avatar}
                    size="md"
                />
                <span className={styles.name} data-article-author-name>
                    {author.name}
                </span>
            </Link>
        );
    }

    const avatars = authors.map<Avatar.Avatar>(({ photos, name }) => ({
        image: photos?.[0]?.photo,
        name,
    }));
    const list = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).formatToParts(
        authors.map(({ name }) => name.split(' ')[0]),
    );

    return (
        <div className={classNames(className, styles.wrapper)} data-article-authors>
            <Link>
                <AvatarGroup size="md" className={styles.avatars} items={avatars} />
            </Link>
            <div className={styles.authors}>
                {list.map(({ type, value }) => {
                    const key = `${type}${value}`;

                    if (type === 'literal') {
                        return (
                            <span key={key} className={styles.separator}>
                                {value}
                            </span>
                        );
                    }

                    return <Link key={key}>{value}</Link>;
                })}
            </div>
        </div>
    );
}

function Link({ children }: { children: ReactNode }) {
    function handleClick(event: MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        scrollToElementById(AUTHORS_CARDS_ID, -HEADER_HEIGHT);
    }

    return (
        <a href={`#${AUTHORS_CARDS_ID}`} className={styles.link} onClick={handleClick}>
            {children}
        </a>
    );
}

function scrollToElementById(id: string, extra = 0) {
    const element = document.getElementById(id);
    if (element) {
        window.scroll({
            top: window.scrollY + element.getBoundingClientRect().top + extra,
            behavior: 'smooth',
        });
    }
}
